// src/config/platformNavStructure.ts
// ✅ FONTE DE VERDADE: estrutura completa de abas e subabas da plataforma
// Usada tanto pelo AdminPlatformManagement quanto pelo usePlatformConfig hook

export interface SubTabDef {
    key: string;
    label: string;
    description: string;
}

export interface TabDef {
    key: string;
    label: string;
    description: string;
    subtabs?: SubTabDef[];
}

// Chave composta de subaba: "tabKey.subtabKey", ex: "questions.study"
export type InterdictKey = string;

// Shape do objeto interdictedTabs legado no backend
export type InterdictedTabsMap = Record<InterdictKey, boolean>;

// ── Novo sistema: três dimensões por aba ──────────────────────
export interface TabConfig {
    hidden: boolean;
    interdicted: boolean;
    allowedUsers: string[]; // array de userId
}

export type TabsConfigMap = Record<string, TabConfig>;

// Estado final resolvido para o usuário atual
export type TabAccess = 'visible' | 'hidden' | 'interdicted';

export interface PlatformConfig {
    interdictedTabs: InterdictedTabsMap;
    tabsConfig?: TabsConfigMap;
}

// ── Estrutura de navegação ────────────────────────────────────
export const NAV_STRUCTURE: TabDef[] = [
    {
        key: 'painel',
        label: 'Painel',
        description: 'Visão geral da jornada de estudos'
    },
    {
        key: 'explore',
        label: 'Flashcards e Apostilas',
        description: 'Conteúdos de estudo',
        subtabs: [
            { key: 'content', label: 'Campos de Estudo', description: 'Decks e ciclos de flashcards' },
            { key: 'saved', label: 'Flashcards Salvos', description: 'Cards marcados para revisão' },
            { key: 'custom', label: 'Estudo Personalizado', description: 'Sessões customizadas' },
            { key: 'create-flashcards', label: 'Criar Flashcards', description: 'Ferramenta de criação de flashcards' },
        ]
    },
    {
        key: 'questions',
        label: 'Questões',
        description: 'Banco de questões de vestibulares',
    },
    {
        key: 'provas',
        label: 'Provas e Simulados',
        description: 'Simulados e provas anteriores',
    },
    {
        key: 'stats',
        label: 'Estatísticas',
        description: 'Acompanhe seu progresso',
        subtabs: [
            { key: 'flashcards', label: 'Flashcards', description: 'Estatísticas de flashcards' },
            { key: 'questions', label: 'Questões', description: 'Desempenho em questões' },
        ]
    },
    {
        key: 'planner',
        label: 'Assistente Graia',
        description: 'Organize seus estudos'
    },
    {
        key: 'groups',
        label: 'Grupos de Estudo',
        description: 'Criar e gerenciar grupos'
    },
];

// ── Helpers legados (compatibilidade) ─────────────────────────

export const isTabInterdicted = (
    map: InterdictedTabsMap | undefined,
    tabKey: string
): boolean => !!(map?.[tabKey]);

export const isSubtabInterdicted = (
    map: InterdictedTabsMap | undefined,
    tabKey: string,
    subtabKey: string
): boolean => {
    if (!map) return false;
    if (map[tabKey]) return true;
    return !!(map[`${tabKey}.${subtabKey}`]);
};

export const getAllInterdictKeys = (): InterdictKey[] => {
    const keys: InterdictKey[] = [];
    for (const tab of NAV_STRUCTURE) {
        keys.push(tab.key);
        if (tab.subtabs) {
            for (const st of tab.subtabs) {
                keys.push(`${tab.key}.${st.key}`);
            }
        }
    }
    return keys;
};

// ── Novo helper: resolveTabAccess ─────────────────────────────

/**
 * Resolve o estado de acesso final de uma aba para o usuário atual.
 *
 * Prioridade:
 *  1. Admin → sempre 'visible'
 *  2. allowedUsers inclui userId → 'visible' (sobrepõe hidden/interdicted)
 *  3. hidden → 'hidden'
 *  4. interdicted → 'interdicted'
 *  5. Padrão → 'visible'
 */
export function resolveTabAccess(
    tabKey: string,
    tabsConfig: TabsConfigMap | undefined,
    userId: string,
    isAdmin: boolean
): TabAccess {
    if (isAdmin) return 'visible';

    const cfg = tabsConfig?.[tabKey];
    if (!cfg) return 'visible';

    if (cfg.allowedUsers?.includes(userId)) return 'visible';
    if (cfg.hidden) return 'hidden';
    if (cfg.interdicted) return 'interdicted';

    return 'visible';
}
